import React, { useState } from 'react';
import {
    Box,
    TextField,
    Button,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemText,
    ListItemButton,
    Chip
} from '@mui/material';
import { Description as DocumentIcon } from '@mui/icons-material';
import { useDataProvider, useNotify } from 'react-admin';

interface DocumentLessonEditorProps {
    content: any;
    onChange: (content: any) => void;
}

const DocumentLessonEditor: React.FC<DocumentLessonEditorProps> = ({ content, onChange }) => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [mediaFiles, setMediaFiles] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    const dataProvider = useDataProvider();
    const notify = useNotify();

    const loadDocumentFiles = async () => {
        setLoading(true);
        try {
            const { data } = await dataProvider.getList('media_files', {
                pagination: { page: 1, perPage: 100 },
                sort: { field: 'createdAt', order: 'DESC' },
                filter: { fileType: 'document' }
            });
            setMediaFiles(data);
        } catch (error) {
            notify('Fehler beim Laden der Dokumente', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = () => {
        loadDocumentFiles();
        setDialogOpen(true);
    };

    const handleSelectDocument = (file: any) => {
        onChange({
            ...content,
            documentFileId: file.id,
            documentFileName: file.originalFilename,
            documentUrl: file.url || `/api/media/${file.uuid}`,
            // Issue #151: documentType war vorher nie gesetzt, der Player
            // hat alles auf 'pdf' geraten und PPT/Word/Excel als leere
            // iframes gerendert. Jetzt Inferenz aus MIME oder Filename,
            // damit der Player den richtigen Render-Pfad nimmt.
            documentType: inferTypeFromFile(file),
        });
        setDialogOpen(false);
    };

    /**
     * Macht aus dem ausgewaehlten MediaFile einen documentType-Token, den
     * der Player versteht: 'pdf', 'pptx', 'docx', 'xlsx', 'other'.
     */
    const inferTypeFromFile = (file: any): string => {
        const mime = String(file.mimeType || '').toLowerCase();
        if (mime === 'application/pdf') return 'pdf';
        if (mime.includes('presentation') || mime.includes('powerpoint')) return 'pptx';
        if (mime.includes('wordprocessing') || mime === 'application/msword') return 'docx';
        if (mime.includes('spreadsheet') || mime === 'application/vnd.ms-excel') return 'xlsx';
        const name = String(file.originalFilename || '').toLowerCase();
        if (name.endsWith('.pdf')) return 'pdf';
        if (name.endsWith('.pptx') || name.endsWith('.ppt')) return 'pptx';
        if (name.endsWith('.docx') || name.endsWith('.doc')) return 'docx';
        if (name.endsWith('.xlsx') || name.endsWith('.xls')) return 'xlsx';
        return 'other';
    };

    const formatFileSize = (bytes: number) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return `${Math.round(bytes / Math.pow(k, i) * 100) / 100} ${sizes[i]}`;
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography variant="h6">Dokument-Lektion</Typography>

            {content?.documentFileName ? (
                <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        Ausgewähltes Dokument:
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                        <DocumentIcon color="primary" />
                        <Typography variant="body1">{content.documentFileName}</Typography>
                    </Box>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={handleOpenDialog}
                        sx={{ mt: 2 }}
                    >
                        Anderes Dokument auswählen
                    </Button>
                </Box>
            ) : (
                <Button
                    variant="contained"
                    startIcon={<DocumentIcon />}
                    onClick={handleOpenDialog}
                >
                    Dokument aus Medienbibliothek auswählen
                </Button>
            )}

            <TextField
                label="Dokument-Beschreibung (optional)"
                multiline
                rows={3}
                fullWidth
                value={content?.description || ''}
                onChange={(e) => onChange({ ...content, description: e.target.value })}
                helperText="Zusätzliche Informationen zum Dokument"
            />

            {/* Document Selection Dialog */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Dokument aus Medienbibliothek auswählen</DialogTitle>
                <DialogContent>
                    {loading ? (
                        <Typography>Lade Dokumente...</Typography>
                    ) : mediaFiles.length === 0 ? (
                        <Typography>Keine Dokumente in der Medienbibliothek gefunden.</Typography>
                    ) : (
                        <List>
                            {mediaFiles.map((file) => (
                                <ListItemButton key={file.id} onClick={() => handleSelectDocument(file)}>
                                    <ListItem>
                                        <DocumentIcon sx={{ mr: 2 }} color="action" />
                                        <ListItemText
                                            primary={file.originalFilename}
                                            secondary={
                                                <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                                                    <Chip label={formatFileSize(parseInt(file.size))} size="small" />
                                                    <Chip label={file.mimeType} size="small" />
                                                </Box>
                                            }
                                        />
                                    </ListItem>
                                </ListItemButton>
                            ))}
                        </List>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Abbrechen</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default DocumentLessonEditor;
